import React, { useCallback, useEffect, useMemo, useRef } from "react";
import format from "date-fns/format";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";

import AddIcon from "@material-ui/icons/Add";

import Table from "../shared/Table";
import { useAppContext } from "../../api/AppContext";
import { useHistory } from "react-router-dom";
import EditIcon from "@material-ui/icons/Edit";
import {
  CHANNEL_BROWSERPLUGIN,
  CHANNEL_CLIENT,
  CHANNEL_EMBEDDED,
  CHANNEL_TEAMSAPP,
  DASHBOARD_TYPE_LOOKUP,
} from "./consts";

const columns = [
  {
    field: "name",
    title: "Company Name",
    type: "string",
  },
  {
    field: "cid",
    title: "CID",
  },
  {
    field: "channel",
    lookup: {
      "browser-plugin": CHANNEL_BROWSERPLUGIN.name,
      client: CHANNEL_CLIENT.name,
      embedded: CHANNEL_EMBEDDED.name,
      "teams-app": CHANNEL_TEAMSAPP.name,
    },
    title: "Channel",
  },
  {
    field: "dashboardType",
    lookup: DASHBOARD_TYPE_LOOKUP,
    title: "Dashboard type",
  },
  {
    field: "freezeUserProgress",
    title: "Freezed",
    type: "boolean",
    render: (props) =>
      props.freezeUserProgress ? (
        <Typography color="textPrimary" variant="body2">
          Yes
        </Typography>
      ) : (
        "No"
      ),
  },
  {
    field: "locale",
    lookup: {
      "en-GB": "English",
      "es-ES": "Spanish",
      "it-IT": "Italian",
      "fr-FR": "French",
    },
    title: "Default Language",
  },
  {
    field: "isEnabled",
    title: "Automated Journey activation",
    type: "boolean",
    render: (props) =>
      props.isEnabled ? (
        <Typography color="textPrimary" variant="body2">
          Yes (activated on{" "}
          {format(props.enabledAt * 1000, "dd MMMM yyyy HH:mm:ss")})
        </Typography>
      ) : (
        "No"
      ),
  },
  {
    field: "trackComputerName",
    title: 'Track "Computer Name"',
    type: "boolean",
    render: (props) =>
      props.trackComputerName ? (
        <Typography color="textPrimary" variant="body2">
          Yes
        </Typography>
      ) : (
        "No"
      ),
  },
  {
    field: "licensed",
    title: "Licensed",
    type: "boolean",
    render: (props) =>
      (props.licensed ?? true) === true ? (
        <Typography color="textPrimary" variant="body2">
          Yes
        </Typography>
      ) : (
        "No"
      ),
  },
  {
    field: "createdAt",
    defaultSort: "desc",
    hidden: true,
  },
];

const Configuration = () => {
  const ref = useRef();

  const {
    fetchConfigurations,
    isConfigurationsLoading,
    configurations,
    deleteConfiguration,
  } = useAppContext();

  useEffect(() => {
    fetchConfigurations();
  }, []);

  const onRowDelete = useCallback(
    async (value) => deleteConfiguration(value),
    [deleteConfiguration]
  );

  const editable = useMemo(
    () => ({
      onRowDelete,
    }),
    [onRowDelete]
  );

  const history = useHistory();

  const tableOptions = useMemo(() => ({ filtering: true }), []);

  const rowActions = useMemo(
    () => [
      {
        icon: (props) => <EditIcon {...props} color="action" />,
        tooltip: "Edit",
        onClick: (event, rowData) => {
          history.push(`/settings/cid-configurations/${rowData.cid}`);
        },
      },
    ],
    [history]
  );

  const tableActions = useMemo(
    () => [
      <Button
        key="add"
        color="primary"
        endIcon={<AddIcon />}
        variant="contained"
        onClick={() => {
          history.push("/settings/cid-configurations/new", {
            isNew: true,
          });
        }}
      >
        Create new Cid
      </Button>,
    ],
    [history]
  );

  return (
    <Box
      boxSizing="border-box"
      display="flex"
      flexDirection="column"
      height="100%"
      p={4}
      width="100%"
    >
      <Table
        actions={rowActions}
        columns={columns}
        data={configurations}
        editable={editable}
        isLoading={isConfigurationsLoading}
        options={tableOptions}
        refetch={fetchConfigurations}
        tableActions={tableActions}
        tableRef={ref}
        title="CID configurations"
      />
    </Box>
  );
};

export default Configuration;
